import { Component, output, signal, computed, model } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { sanitizeText } from 'src/app/common/input-utils';

export interface Asistente {
  nombre: string;
  cargo: string;
}

/**
 * DUMB COMPONENT: BloqueAsistentes
 *
 * Panel para gestionar la lista de asistentes en una visita de campo.
 * Permite agregar y eliminar asistentes (nombre + cargo).
 *
 * HU1132419 — Legacy: CreateOrUpdateCumplimientoViewImpl.java
 * - Sección "Asistentes a la Visita"
 * - Campos: nombre (String), cargo (String)
 * - El usuario puede agregar múltiples asistentes
 */
@Component({
  selector: 'app-bloque-asistentes',
  standalone: true,
  imports: [CommonModule, FormsModule, ButtonModule, InputTextModule],
  template: `
    <div class="bloque-asistentes">
      <h4 class="text-sm font-semibold text-gray-700 mb-3">
        Agregar Asistentes
      </h4>

      <!-- Formulario de ingreso -->
      <div class="flex flex-col sm:flex-row sm:flex-wrap items-stretch sm:items-center gap-2 sm:gap-3 mb-3">
        <input
          pInputText
          type="text"
          [ngModel]="nuevoNombre()"
          (ngModelChange)="nuevoNombre.set($event)"
          (change)="nuevoNombre.set(sanitizeText(nuevoNombre()))"
          placeholder="Nombre completo"
          class="w-full sm:flex-1"
          maxlength="200"
        />
        <input
          pInputText
          type="text"
          [ngModel]="nuevoCargo()"
          (ngModelChange)="nuevoCargo.set($event)"
          (change)="nuevoCargo.set(sanitizeText(nuevoCargo()))"
          placeholder="Cargo / Rol"
          class="w-full sm:flex-1"
          maxlength="150"
        />
        <p-button
          label="Agregar"
          icon="pi pi-plus"
          severity="secondary"
          size="small"
          styleClass="w-full sm:w-auto"
          [disabled]="!puedeAgregar()"
          (onClick)="agregarAsistente()"
        >
        </p-button>
      </div>

      <!-- Lista de asistentes -->
      @if (asistentes().length > 0) {
        <ul class="divide-y divide-gray-100 border rounded">
          @for (asistente of asistentes(); track $index) {
            <li class="flex items-center justify-between px-3 py-2 text-sm">
              <span>
                <span class="font-medium">{{ asistente.nombre }}</span>
                <span class="text-gray-500 ml-2">— {{ asistente.cargo }}</span>
              </span>
              <p-button styleClass="min-w-[44px] min-h-[44px]"
                icon="pi pi-trash"
                severity="danger"
                [text]="true"
                size="small"
                (onClick)="eliminarAsistente($index)"
              >
              </p-button>
            </li>
          }
        </ul>
      } @else {
        <p class="text-sm text-gray-400 italic">
          No se han agregado asistentes.
        </p>
      }
    </div>
  `
})
export class BloqueAsistentesComponent {
  readonly sanitizeText = sanitizeText;

  /** Emite la lista actualizada cuando cambia */
  asistentesChange = output<Asistente[]>();

  readonly asistentes = signal<Asistente[]>([]);

  readonly nuevoNombre = signal('');
  readonly nuevoCargo = signal('');

  readonly puedeAgregar = computed(
    () =>
      this.nuevoNombre().trim().length > 0 &&
      this.nuevoCargo().trim().length > 0
  );

  agregarAsistente(): void {
    if (!this.puedeAgregar()) return;
    const nuevo: Asistente = {
      nombre: sanitizeText(this.nuevoNombre().trim()),
      cargo: sanitizeText(this.nuevoCargo().trim())
    };
    this.asistentes.update((lista) => [...lista, nuevo]);
    this.nuevoNombre.set('');
    this.nuevoCargo.set('');
    this.asistentesChange.emit(this.asistentes());
  }

  eliminarAsistente(index: number): void {
    this.asistentes.update((lista) => lista.filter((_, i) => i !== index));
    this.asistentesChange.emit(this.asistentes());
  }
}
